import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {BehaviorSubject, merge, Observable, of, Subject, Subscription} from 'rxjs';
import {
    AbstractControl,
    AsyncValidatorFn,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidatorFn,
    Validators
} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {Config} from 'smag-commons/cfg/connector/merge';
import {
    EDITOR_BUILTIN_FUNCTIONS,
    EDITOR_BUILTIN_TYPES,
    MERGE_DEFAULT_DEVICE_MODE,
    MERGE_DEVICE_MODES
} from '../connectors';
import * as _ from 'lodash';
import {isBlank, keySorter} from '../../../commons/commons';
import {map, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {validateNotBlank} from '../../../commons/validators';
import {MatRadioChange} from '@angular/material/radio';
import {Features} from '../expression-editor/expression-editor.component';
import {APP_CONFIG_TOKEN, AppConfig} from '../../../app.config';
import {I18NService} from '../../../i18n/i18n.service';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../../shared/i18n-mat-paginator-intl';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {SmagApiService} from '../../../api/smag-api.service';

interface DeviceConfig {
    devId: string;
    form: FormGroup;
    valueSubscription: Subscription;
    statusSubscription: Subscription;
}

interface PagedDevices {
    data: DeviceConfig[];
    total: number;
}

const VARIABLE_ACCESSORS = ['m', 's']; // "measures", "state"
const EMIT_FUNCTION = 'emit';

const DEVICE_ADD_DIALOG_MAX_WIDTH = 500;
const DEVICE_RENAME_DIALOG_MAX_WIDTH = 500;
const DEVICE_DELETE_DIALOG_MAX_WIDTH = 500;
const DEVICE_EXPRESSION_DIALOG_MIN_WIDTH = 800;
const DEVICE_EXPRESSION_DIALOG_MAX_WIDTH = 900;
const PAGE_SIZE = 5;
const PAGE_SIZES = [5, 10, 15, 30, 50];

const EXPRESSION_HELP = 'Input measures are available through measure-value map "m".__{merge}';
const ALGORITHM_HELP = 'Input measures are available through measure-value map "m". A global state can be tracked using the map "s". Final value must be returned using the emit() built-in method.__{merge}';

@Component({
    selector: 'app-merge-config',
    templateUrl: './merge-config.component.html',
    styleUrls: ['./merge-config.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Virtual devices per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class MergeConfigComponent implements OnInit, AfterViewInit, OnDestroy {

    private destroyed = new Subject<void>();
    private reloadDevsPages: Subject<void>;
    @ViewChild('addDevice') private addDeviceDialog: TemplateRef<any>;
    @ViewChild('renameDevice') private renameDeviceDialog: TemplateRef<any>;
    @ViewChild('deleteDevice') private deleteDeviceDialog: TemplateRef<any>;
    @ViewChild('deviceExpression') private deviceExpressionDialog: TemplateRef<any>;
    @ViewChild('devsPaginator') private devsPaginator: MatPaginator;

    pagedDevs: BehaviorSubject<PagedDevices>;

    @Input('id') id: string;
    @Input('config') config: Config;
    @Output('configChange') configEmitter: EventEmitter<Config> = new EventEmitter<Config>();
    @Output('valid') validEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    cfg: Config;
    form: FormGroup;
    devices: DeviceConfig[];
    allMeasures: string[];

    // exported in HTML
    MERGE_DEVICE_MODES = MERGE_DEVICE_MODES;
    EXPRESSION_HELP = EXPRESSION_HELP;
    ALGORITHM_HELP = ALGORITHM_HELP;
    PAGE_SIZE = PAGE_SIZE;
    PAGE_SIZES = PAGE_SIZES;

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly appCfg: AppConfig,
                private readonly apiService: SmagApiService,
                private readonly i18n: I18NService,
                private fb: FormBuilder,
                private readonly dialog: MatDialog,
                private ngZone: NgZone,
                private readonly changeDetectorRef: ChangeDetectorRef) {
        this.reloadDevsPages = new Subject<void>();
        this.pagedDevs = new BehaviorSubject<PagedDevices>(null);
        this.allMeasures = [];
    }

    ngOnInit() {
        // NOTE: config is not supposed to be changed externally outside of component's creation
        this.cfg = _.cloneDeep(this.config);
        this.form = this.fb.group({});
        this.devices = Object.keys(this.cfg.devices).map(devId => this.mkDeviceConfig(devId));
        this.devices.sort(keySorter('devId'));
        this.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            this.updateConfig();
        });
        this.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        // do not send an initial config update, as it may trigger a (useless) REST call
        this.updateValidity();

        // load *all* devices from all connectors (except this one)
        // return all but this connector's devices
        this.apiService.getConnectorDevices().pipe(
            map(connectorDevices => _.sortBy(_.flatMap(connectorDevices, (devs, connId) => connId !== this.id ?
                devs.map(d => d.device) :
                [])))
        ).subscribe(measures => {
            this.allMeasures = measures;
        });
    }

    ngAfterViewInit() {
        merge(this.devsPaginator.page,
            this.reloadDevsPages.pipe(tap(() => this.devsPaginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() =>
                this.mkPagedDevices(this.devsPaginator.pageSize, this.devsPaginator.pageIndex).pipe(
                    startWith(null as PagedDevices) // trigger loading spinner
                )),
            takeUntil(this.destroyed)
        ).subscribe(pagedDevs => {
            if (pagedDevs) {
                this.pagedDevs.next(pagedDevs);
                this.changeDetectorRef.detectChanges();
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.complete();
        this.destroyed.next();
    }

    trackByDevId(index: number, devCfg: DeviceConfig): string {
        return devCfg.devId;
    }

    trackByMeasure(index: number, measure: string): string {
        return measure;
    }

    isValidDevice(devCfg: DeviceConfig): boolean {
        if (!devCfg.form.valid) {
            return false;
        }
        const dev = this.cfg.devices[devCfg.devId];
        return (!!dev.expression && !!dev.expression.trim().length) ||
            (!!dev.algorithm && !!dev.algorithm.trim().length);
    }

    onClickAddDevice() {
        this.dialog.open(this.addDeviceDialog, {
            data: {
                idFormControl: new FormControl('', [validateNotBlank], [this.mkDeviceUniquenessValidator()])
            },
            maxWidth: `${DEVICE_ADD_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(id => {
            if (id) {
                this.addDevice(id);
            }
        });
    }

    onClickRenameDevice(devId: string) {
        this.dialog.open(this.renameDeviceDialog, {
            data: {
                devId,
                idFormControl: new FormControl(devId, [validateNotBlank], [this.mkDeviceUniquenessValidator(devId)])
            },
            maxWidth: `${DEVICE_RENAME_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(newName => {
            // check if we are actually changing name
            if (newName && newName !== devId) {
                this.renameDevice(devId, newName);
            }
        });
    }

    onClickDeleteDevice(devId: string) {
        this.dialog.open(this.deleteDeviceDialog, {
            data: {
                devId
            },
            maxWidth: `${DEVICE_DELETE_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(del => {
            if (del) {
                this.deleteDevice(devId);
            }
        });
    }

    onClickDeviceExpression(dev: DeviceConfig) {
        const measures = this.cfg.devices[dev.devId].measures;
        const algo = this.cfg.devices[dev.devId].algorithm;
        const useAlgo = !!algo && !!algo.trim().length;
        const form = this.fb.group({
            useAlgo: new FormControl(useAlgo, []),
            expression: new FormControl(!useAlgo ? this.cfg.devices[dev.devId].expression : this.cfg.devices[dev.devId].algorithm, [validateNotBlank])
        });
        this.dialog.open(this.deviceExpressionDialog, {
            data: {
                devId: dev.devId,
                form,
                measures,
                features: this.mkEditorFeatures(measures)
            },
            minWidth: `${DEVICE_EXPRESSION_DIALOG_MIN_WIDTH}px`,
            maxWidth: `${DEVICE_EXPRESSION_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe(keep => {
            if (keep) {
                this.setDeviceExpression(dev,
                    form.controls.useAlgo.value,
                    form.controls.expression.value);
            }
        });
    }

    onChangeExpressionVSAlgorithm(form: FormGroup, event: MatRadioChange) {
        form.controls.expression.setValue(null);
        form.markAsUntouched();
    }

    private get isValid(): boolean {
        return this.form.valid && this.devices.every(dev => this.isValidDevice(dev));
    }

    private updateConfig() {
        this.configEmitter.emit(this.cfg);
    }

    private updateValidity() {
        this.validEmitter.emit(this.isValid);
    }

    private mkEditorFeatures(measures: string[]): Features {
        const cfg = this.appCfg.exprEditor;
        return {
            keywords: _.fromPairs([
                ...EDITOR_BUILTIN_TYPES.map(t => [t, {color: cfg.builtinTypesColor, bold: cfg.builtinsBold}]),
                ...EDITOR_BUILTIN_FUNCTIONS.map(f => [f, {color: cfg.builtinFunctionsColor, bold: cfg.builtinsBold}]),
                [EMIT_FUNCTION, {color: cfg.builtinFunctionsColor, bold: cfg.builtinsBold}],
                ...VARIABLE_ACCESSORS.map(f => [f, {
                    color: cfg.variableAccessorsColor,
                    bold: cfg.variableAccessorBold
                }]),
                ...measures.map(m => [m, {color: cfg.variablesColor, bold: cfg.variablesBold}])
            ])
        };
    }

    private addDevice(devId: string) {
        // config must be updated in order for mkDeviceConfig() to work
        this.cfg.devices[devId] = {
            mode: MERGE_DEFAULT_DEVICE_MODE,
            expression: null,
            algorithm: null,
            measures: []
        };
        this.devices.push(this.mkDeviceConfig(devId));
        this.devices.sort(keySorter('devId'));
        this.reloadDevsPages.next();
        this.updateConfig();
        this.updateValidity();
    }

    private renameDevice(devId: string, newName: string) {
        const dev = this.devices.find(dev => dev.devId === devId);
        dev.devId = newName;
        this.cfg.devices[newName] = this.cfg.devices[devId];
        delete this.cfg.devices[devId];
        this.devices.sort(keySorter('devId'));
        this.reloadDevsPages.next();
        this.updateConfig();
        // validity does not change, as new name has already been validated
    }

    private deleteDevice(devId: string) {
        const idx = this.devices.findIndex(dev => dev.devId === devId);
        this.devices[idx].valueSubscription.unsubscribe();
        this.devices[idx].statusSubscription.unsubscribe();
        this.devices.splice(idx, 1);
        delete this.cfg.devices[devId];
        this.reloadDevsPages.next();
        this.updateConfig();
        // validity may change (e.g. in case an invalid device is deleted)
        this.updateValidity();
    }

    private setDeviceExpression(dev: DeviceConfig,
                                useAlgorithm: boolean,
                                expression: string) {
        this.cfg.devices[dev.devId].expression = !useAlgorithm ? expression.trim() : null;
        this.cfg.devices[dev.devId].algorithm = useAlgorithm ? expression.trim() : null;
        this.updateConfig();
        this.updateValidity();
    }

    private mkDeviceConfig(devId: string): DeviceConfig {
        const cfg = {
            devId: devId,
            form: this.fb.group({
                mode: new FormControl(this.cfg.devices[devId].mode, [Validators.required]),
                measures: new FormControl(this.cfg.devices[devId].measures, [minElementsValidator(2)]),
                uom: new FormControl(this.cfg.devices[devId].uom, [])
            })
        } as DeviceConfig;
        cfg.valueSubscription = cfg.form.valueChanges.pipe(takeUntil(this.destroyed)).subscribe(value => {
            const dev = this.cfg.devices[cfg.devId];
            dev.mode = value['mode'];
            dev.measures = value['measures'];
            dev.uom = value['uom'] && value['uom'].trim().length ? value['uom'].trim() : null;
            this.updateConfig();
        });
        cfg.statusSubscription = cfg.form.statusChanges.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.updateValidity();
        });
        return cfg;
    }

    private mkDeviceUniquenessValidator(ignoreDevice?: string): AsyncValidatorFn {
        return ctrl => {
            const device = !isBlank(ctrl.value) ? ctrl.value.trim() : null;
            if (!device || (!!ignoreDevice && device === ignoreDevice)) {
                return of(null);
            }
            return this.isDeviceDuplicate(device).pipe(
                map(isDuplicate => isDuplicate ? {'duplicate': true} : null)
            );
        };
    }

    private isDeviceDuplicate(device: string): Observable<boolean> {
        // check for duplicates inside this very connector, using *current* values
        if (this.devices.find(devCfg => devCfg.devId === device)) {
            return of(true);
        }
        return this.apiService.checkConnectorDeviceExistence(device).pipe(
            switchMap(resp => resp.value ?
                this.apiService.findConnectorByDevice(device).pipe(
                    map(idResp => idResp?.id !== this.id) // ignore "this very connector" (already checked against current values)
                ) :
                of(false))
        );
    }

    private mkPagedDevices(size: number, page: number): Observable<PagedDevices> {
        let pagedDevs = [];
        for (let i = 0; i < this.devices.length; i += size) {
            pagedDevs.push(this.devices.slice(i, i + size));
        }
        return of({
            data: pagedDevs[page],
            total: this.devices.length
        } as PagedDevices);
    }
}

function minElementsValidator(min: number): ValidatorFn {
    return (ctrl: AbstractControl) => {
        if (ctrl.value.length < min) {
            return {not_enough_elements: true};
        }
        return null;
    };
}
